import { ShieldCheck, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { SHOP_CATEGORIES } from '../../lib/shop-taxonomy.js';

/**
 * Startseite für Mandanten mit eigenen Startseiten-Texten (a-045).
 * Hero mit Logo und Mandanten-Farben, Kategorie-Einstieg, Trust-Zeile, CTA.
 * Nutzt das bestehende Designsystem (CSS-Variablen --primary/--secondary).
 */
export function StorefrontHome({ tenant, texts, categories = [], onNavigate }) {
  // Farben kommen über normalizeTenant immer aus tenants; CSS-Variablen --primary/--secondary.
  const tenantName = tenant?.name || '';

  const visibleCategories = SHOP_CATEGORIES.filter((category) => {
    const data = categories.find((c) => c.slug === category.slug);
    return (data?.productCount || data?.product_count || 0) > 0;
  });

  return (
    <div data-testid="storefront-home" className="mx-auto w-full max-w-[1440px] px-4 py-8 sm:px-6 lg:px-8 space-y-16">
      {/* HERO */}
      <section
        data-testid="storefront-hero"
        className="relative overflow-hidden rounded-3xl border border-[var(--border)] bg-white p-8 sm:p-12"
        style={{ borderTop: '6px solid var(--primary)' }}
      >
        <div className="max-w-3xl space-y-6">
          {tenant?.logoUrl && (
            <img
              data-testid="storefront-logo"
              src={tenant.logoUrl}
              alt={tenantName}
              className="h-12 w-auto object-contain"
            />
          )}
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-5xl">
            {texts.hero_title}
          </h1>
          {texts.hero_subtitle && (
            <p className="text-lg text-[var(--secondary)] leading-relaxed">{texts.hero_subtitle}</p>
          )}
          {texts.trust_line && (
            <p data-testid="storefront-trust-line" className="text-sm font-semibold" style={{ color: 'var(--secondary)' }}>
              {texts.trust_line}
            </p>
          )}
        </div>
      </section>

      {/* KATEGORIE-EINSTIEG */}
      <section className="space-y-8">
        <div>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-4xl">
            {texts.section_title}
          </h2>
          {texts.section_subtitle && (
            <p className="mt-2 text-sm text-[var(--secondary)]">{texts.section_subtitle}</p>
          )}
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visibleCategories.map((category) => (
            <button
              key={category.slug}
              type="button"
              data-testid="storefront-category"
              onClick={() => onNavigate(`/catalog/${category.slug}`)}
              className="group flex flex-col justify-between rounded-2xl border border-[var(--border)] bg-white p-6 text-left transition-all hover:border-[var(--primary)] hover:shadow-md cursor-pointer"
            >
              <div className="space-y-3">
                <h3 className="font-display text-xl font-bold text-[var(--foreground)] group-hover:text-[var(--primary)]">
                  {category.label}
                </h3>
                <p className="text-sm text-[var(--secondary)] line-clamp-2">{category.description}</p>
              </div>
              <div className="mt-6 flex items-center justify-between border-t border-[var(--border)] pt-4 text-xs font-bold text-[var(--foreground)] group-hover:text-[var(--primary)]">
                <span>Zur Kategorie</span>
                <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="storefront-cta"
        className="flex flex-col items-start gap-4 rounded-3xl bg-[var(--surface-container-low)] p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10"
      >
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--primary)] text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="space-y-1">
            <h2 className="font-display text-xl font-bold text-[var(--foreground)]">{texts.cta_title}</h2>
            {texts.cta_text && <p className="text-sm text-[var(--secondary)]">{texts.cta_text}</p>}
          </div>
        </div>
        <Button onClick={() => onNavigate('/catalog')}>{texts.cta_button || 'Zum Katalog'}</Button>
      </section>
    </div>
  );
}
